import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import type { FeatureRequest } from '@/hooks/useFeatures';

interface FeatureCardProps {
  feature: FeatureRequest;
  rank: number;
  hasVoted: boolean;
  isLoggedIn: boolean;
  onVote: (featureId: number) => void;
  onUnvote: (featureId: number) => void;
  isVoting?: boolean;
}

const statusConfig: Record<string, { label: string; color: 'primary' | 'warning' | 'success' | 'error' }> = {
  open: { label: '待开发', color: 'primary' },
  in_progress: { label: '开发中', color: 'warning' },
  done: { label: '已完成', color: 'success' },
  rejected: { label: '已拒绝', color: 'error' },
};

function maskName(name: string | null): string {
  if (!name) return '匿名用户';
  const chars = [...name]; // Handle unicode (Chinese characters)
  if (chars.length <= 1) return chars[0] + '****';
  return chars[0] + '****' + chars[chars.length - 1];
}

export default function FeatureCard({
  feature,
  rank,
  hasVoted,
  isLoggedIn,
  onVote,
  onUnvote,
  isVoting,
}: FeatureCardProps) {
  const status = statusConfig[feature.status] || statusConfig.open;
  const createdDate = new Date(feature.created_at).toLocaleDateString('zh-CN');

  const handleVoteClick = () => {
    if (!isLoggedIn || isVoting) return;
    if (hasVoted) {
      onUnvote(feature.id);
    } else {
      onVote(feature.id);
    }
  };

  return (
    <Card
      sx={{
        display: 'flex',
        gap: 2,
        p: 2,
        bgcolor: 'background.paper',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      {/* Rank */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'flex-start',
          pt: 1,
          minWidth: 32,
        }}
      >
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          #{rank}
        </Typography>
      </Box>

      {/* Vote Button */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 48,
        }}
      >
        <Tooltip title={isLoggedIn ? (hasVoted ? '取消投票' : '投票') : '请先登录'}>
          <span>
            <IconButton
              onClick={handleVoteClick}
              disabled={!isLoggedIn || isVoting}
              sx={{
                bgcolor: hasVoted ? 'success.main' : 'action.selected',
                color: hasVoted ? 'white' : 'text.secondary',
                '&:hover': {
                  bgcolor: hasVoted ? 'success.dark' : 'action.hover',
                },
                '&:disabled': {
                  bgcolor: 'action.disabledBackground',
                },
              }}
            >
              <KeyboardArrowUpIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="body1" fontWeight={600}>
          {feature.vote_count}
        </Typography>
      </Box>

      {/* Content */}
      <CardContent sx={{ flex: 1, p: 0, '&:last-child': { pb: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
            {feature.title}
          </Typography>
          <Chip
            label={status.label}
            color={status.color}
            size="small"
            variant="outlined"
          />
        </Box>

        {feature.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {feature.description}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            @{maskName(feature.author_name)}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
            {createdDate}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
